// runtime-intro.js — the one-time broadcast-style match intro sequence that is
// burnt into the recording. Split from runtime-core.js. Kept separate from the
// match-flow module so runtime-recording can trigger it without creating an
// import cycle (recording → intro, never intro → recording).
import { get } from 'svelte/store';
import { activeMatch } from './svelte/store.js';
import { persist, appSettings, getMeta } from './store.js';
import { buildIntroPayload, INTRO_TOTAL_MS, validateIntroPayload } from './intro-sequence.js';
import { triggerAnimation as triggerAnim } from './broadcast/overlay-state.js';
import { currentInnings, formatOvers } from './scoring-logic.js';
import { teamColor, teamLogo } from './runtime-teams.js';
import { econ } from './runtime-draw-utils.js';

// ─── MATCH INTRO SEQUENCE ───────────────────────────────────────────────
// Fired automatically the FIRST time the user taps REC for a given match.
// Plays a 6-card broadcast-style opener that's burnt into the video:
//   1. MATCH_INTRO — almost full-screen, shows teams + overs + venue
//   2. MATCH_TOSS  — who won the toss and what they chose
//   3. PLAYER_LIST — both teams' playing XI
//   4. NEW_BATTER  — striker intro
//   5. NEW_BATTER  — non-striker intro
//   6. NEW_BOWLER  — opening bowler intro
// Each animation queues behind the previous one via triggerAnim's internal
// queue. Total runtime ≈ 33 seconds. The flag `_introPlayed` on the match
// object guarantees it only runs once per match (auto-split's chained restart
// passes opts.skipPreflight which also skips this).
export let playMatchIntroSequence = function(options = {}) {
  const m = get(activeMatch);
  if (!m) return;
  if (options.legacy !== true) {
    if (options.automatic && m._introPlayedAt) return false;
    const payload = buildIntroPayload(m, appSettings?.venue || "Rohini Sec 28", getMeta);
    const checked = validateIntroPayload(payload);
    if (!checked.ok) {
      console.warn("Intro data incomplete:", checked.error);
      return false;
    }
    if (options.automatic) {
      m._introPlayedAt = Date.now();
      m._introPlayed = true;
      persist({ immediate: true });
    }
    triggerAnim({ type: "INTRO_SEQUENCE", payload: checked.value, _introPayload: checked.value, _introPayloadChecked: true, duration: INTRO_TOTAL_MS });
    return true;
  }
  const inn = currentInnings(m);
  if (!inn) return;

  // 1. INTRO card (almost full screen with team logos, overs count, venue)
  triggerAnim({
    type: "MATCH_INTRO",
    teamAName: m.teamA.name,
    teamBName: m.teamB.name,
    teamAColor: teamColor(m.teamA.name),
    teamBColor: teamColor(m.teamB.name),
    teamALogo: teamLogo(m.teamA.name),
    teamBLogo: teamLogo(m.teamB.name),
    overs: m.overs,
    venue: appSettings?.venue || "Rohini Sec 28",
    duration: 7000
  });

  // 2. TOSS card
  if (m.toss && m.toss.winner) {
    const tossWinnerName = m.toss.winner === "A" ? m.teamA.name : m.teamB.name;
    triggerAnim({
      type: "MATCH_TOSS",
      winner: tossWinnerName,
      choice: m.toss.choice === "bat" ? "BAT" : "BOWL",
      color: teamColor(tossWinnerName),
      logo: teamLogo(tossWinnerName),
      duration: 7000
    });
  }

  // 3. Playing XI for both teams (existing animation)
  triggerAnim({
    type: "PLAYER_LIST",
    title: "PLAYING XI",
    teamA: m.teamA,
    teamB: m.teamB,
    duration: 8000
  });

  // 4. Striker intro
  if (inn.currentStriker) {
    triggerAnim({ type: "NEW_BATTER", name: inn.currentStriker, role: "STRIKER", duration: 4000 });
  }
  // 5. Non-striker intro
  if (inn.currentNonStriker) {
    triggerAnim({ type: "NEW_BATTER", name: inn.currentNonStriker, role: "NON-STRIKER", duration: 4000 });
  }
  // 6. Opening bowler intro
  if (inn.currentBowler) {
    const b = inn.bowlers.find(x => x.name === inn.currentBowler);
    triggerAnim({
      type: "NEW_BOWLER",
      name: inn.currentBowler,
      overs: b ? formatOvers(b.balls) : "0.0",
      runs: b ? b.runs : 0,
      wickets: b ? b.wickets : 0,
      econ: b ? econ(b.runs, b.balls) : "—",
      duration: 5000
    });
  }
}
